import {
  AccountInfo,
  Configuration,
  PublicClientApplication,
} from '@azure/msal-browser';

import { createCodedError } from '~/common/errors/CodedError';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { SharepointStatusType } from '~/enums/SharepointType';
import { JsonApiErrorResponse } from '~/redux/api/ApiTypes';
import { testIsAxiosError } from '~/redux/api/utils';
import { scribyl } from '~/services/serviceInstances';

/**
 * MSAL request scopes
 * Keep aligned with your Azure app registration.
 */
const loginRequest = {
  scopes: ['https://graph.microsoft.com/FileStorageContainer.Selected'],
};

/**
 * Params for getting SharePoint embedded URL
 */
interface ScribylUploadParams {
  documentInfo: DocumentInfo;
  redirectUrl: string;
  openMode?: 'desktop' | 'web';
}

export interface DocumentInfo {
  documentId: string;
  documentVersionId: string;
  documentType: string;
  documentName?: string;
  ticketId: string;
}

interface MsIdentity {
  accessToken: string;
  account: AccountInfo;
}

export interface SessionResponse {
  sessionUsers: Array<SessionUser>;
}

interface SessionUser {
  evisortUserId: string;
}

export interface SPEConfiguration {
  id?: string;
  tenantId: string;
  sharepointUrl: string;
  status?: SharepointStatusType;
}

type SessionQuery = Partial<DocumentInfo>;
/**
 * Create a SINGLETON MSAL instance for the whole app.
 * Use a stable redirectUri (do NOT vary per call) to avoid interaction_in_progress issues.
 */
const msalConfig: Configuration = {
  auth: {
    clientId: getEnvVariable('EVISORT_SPE_CLIENT_ID'),
    authority: 'https://login.microsoftonline.com/organizations',
    redirectUri:
      (typeof window !== 'undefined' && window.location?.origin) || undefined,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

/**
 * Lazy initialization of MSAL instance.
 * This prevents crypto errors when the module is imported but not used.
 */
let msalInstance: PublicClientApplication | null = null;
let msalInitialized: Promise<void> | null = null;

/**
 * Check if crypto is available in the current context.
 * Web Crypto API is only available in secure contexts (HTTPS or localhost).
 */
function isCryptoAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.crypto !== 'undefined' &&
    typeof window.crypto.subtle !== 'undefined'
  );
}

function getMsalInstance(): PublicClientApplication {
  if (!isCryptoAvailable()) {
    throw new Error(
      'SharePoint integration requires a secure context (HTTPS or localhost). ' +
        'The current origin does not support the Web Crypto API required by Microsoft Authentication. ' +
        'Please access the application via HTTPS or localhost.',
    );
  }

  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    msalInitialized = msalInstance.initialize();
  }
  return msalInstance;
}

async function ensureMsalInitialized(): Promise<void> {
  getMsalInstance();
  await msalInitialized;
}

/**
 * A simple module-level lock to ensure only ONE interactive flow runs at a time.
 */
let interactiveFlow: Promise<AccountInfo | null> | null = null;

/**
 * Set by resetMsalSession() after a failed backend call so that the *next*
 * loginPopup forces the AAD account picker.
 */
let forceAccountPickerOnNextLogin = false;

async function ensureLoggedIn() {
  await ensureMsalInitialized();
  const instance = getMsalInstance();

  // Reuse existing account if available
  const accounts = instance.getAllAccounts();
  if (accounts.length > 0) {
    instance.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  // Serialize loginPopup if multiple callers race
  if (!interactiveFlow) {
    const shouldPromptSelectAccount = forceAccountPickerOnNextLogin;
    forceAccountPickerOnNextLogin = false;

    interactiveFlow = (async () => {
      const resp = await instance.loginPopup({
        ...loginRequest,
        ...(shouldPromptSelectAccount ? { prompt: 'select_account' } : {}),
      });
      if (resp.account) instance.setActiveAccount(resp.account);
      return resp.account ?? null;
    })().finally(() => {
      interactiveFlow = null;
    });
  }
  return interactiveFlow;
}

export async function acquireAccessToken(isRetry = false): Promise<MsIdentity> {
  await ensureMsalInitialized();
  const instance = getMsalInstance();

  let account = instance.getActiveAccount();
  if (!account) {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0];
      instance.setActiveAccount(account);
    }
  }
  if (!account) {
    await ensureLoggedIn();
    account = instance.getActiveAccount()!;
  }

  try {
    // Prefer silent token
    const silent = await instance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return {
      accessToken: silent.accessToken,
      account,
    };
  } catch (e: any) {
    if (!isRetry) {
      await resetMsalSession();
      return await acquireAccessToken(true); // mark as retry
    }
    throw e; // if we already retried, give up
  }
}

/**
 * Resets MSAL session data by clearing all accounts and cache.
 * This forces a new login on the next authentication attempt.
 */
async function resetMsalSession(): Promise<void> {
  await ensureMsalInitialized();
  const instance = getMsalInstance();

  const accounts = instance.getAllAccounts();
  await Promise.all(
    accounts.map((account) => instance.clearCache({ account })),
  );
  instance.setActiveAccount(null);

  // Clear MSAL cache from sessionStorage
  // Since MSAL uses sessionStorage for caching (cacheLocation: 'sessionStorage'),
  // clearing these keys will remove all cached accounts and tokens
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const keys = Object.keys(window.sessionStorage);
    keys.forEach((key) => {
      if (key.startsWith('msal.')) {
        window.sessionStorage.removeItem(key);
      }
    });
  }

  // Arm the next loginPopup to force the AAD account picker.
  forceAccountPickerOnNextLogin = true;
}

/**
 * Public API: returns the SharePoint embedded URL from backend.
 * IMPORTANT: avoid calling interactive APIs here. We only request a token through acquireAccessToken(),
 * which is silent-first and serializes popup when absolutely needed.
 *
 * If the backend request fails, the MSAL session will be reset to force a new login,
 * which helps catch cases where the user logged in with the wrong account.
 */
export async function getSharepointEmbeddedUrl({
  documentInfo,
  redirectUrl,
  openMode,
}: ScribylUploadParams): Promise<string | undefined> {
  const msIdentity = await acquireAccessToken();

  try {
    const response = await scribyl.post(`/sharepoint/upload`, {
      params: {
        documentInfo,
        redirectUrl,
        msIdentity,
        ...(openMode && { openMode }),
      },
    });
    return response.data;
  } catch (error) {
    // If the request fails, reset MSAL session to force a new login
    // This catches cases where the user logged in with the wrong account
    await resetMsalSession();
    if (testIsAxiosError<JsonApiErrorResponse>(error) && error.response) {
      const errorResponse = error.response.data.errors?.[0];
      throw createCodedError({
        message: errorResponse?.code,
        statusCode: errorResponse?.status,
        title: errorResponse?.title,
      });
    }
    throw error;
  }
}

export async function getEditSessionStatus({
  documentId,
  documentVersionId,
}: SessionQuery): Promise<SessionResponse> {
  const response = await scribyl.get(
    `/sharepoint/sessions?documentId=${documentId}&versionId=${documentVersionId}`,
  );
  return response.data as SessionResponse;
}

export type UnlockSharepointDocumentParams = {
  documentId: string;
  versionId: string;
};

export async function unlockSharepointDocument(
  params: UnlockSharepointDocumentParams,
): Promise<void> {
  await scribyl.post('/unlock', params);
}
